import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';
import { useSavedEvents } from '../contexts/SavedEventsContext';
import type { CalendarEvent } from '../types';
import './Dashboard.css';

type EventRow = CalendarEvent & { category?: { name: string } | null };

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const { savedIds } = useSavedEvents();
  const navigate = useNavigate();
  const [myEvents, setMyEvents] = useState<EventRow[]>([]);
  const [savedEvents, setSavedEvents] = useState<EventRow[]>([]);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // `user` from Supabase gets a new object reference on TOKEN_REFRESHED when you tab back;
  // only re-fetch when the actual id or saved list *contents* change.
  const userId = user?.id ?? null;
  const savedIdsKey = useMemo(() => [...savedIds].sort().join(','), [savedIds]);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login?redirect=/dashboard', { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [mine, saved] = await Promise.all([
        supabase
          .from('events')
          .select('*, category:event_categories(name)')
          .eq('created_by', userId)
          .order('created_at', { ascending: false })
          .limit(30),
        savedIds.length
          ? supabase
              .from('events')
              .select('*, category:event_categories(name)')
              .in('id', savedIds)
              .order('start_date', { ascending: true })
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (cancelled) return;
      if (mine.error) setDashboardError(mine.error.message);
      if (saved.error) setDashboardError(saved.error.message);
      setMyEvents((mine.data as EventRow[]) ?? []);
      setSavedEvents((saved.data as EventRow[]) ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, savedIdsKey]);

  if (authLoading || loading || !user) {
    return (
      <div className="dashboard-page">
        <p className="dashboard-loading">Loading dashboard…</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <header className="dashboard-hero">
        <p className="dashboard-eyebrow">Contributor dashboard</p>
        <h1>App home for planning tools</h1>
        <p>
          Open the tool you need: submit events, Your Year Ahead planner, saved events and settings.
        </p>
      </header>

      <section className="dashboard-app-grid">
        <Link to="/submit" className="dashboard-app-card dashboard-app-link">
          <p>App</p>
          <h3>Submit event</h3>
          <small>Create a new draft submission</small>
        </Link>
        <Link to="/scheduler" className="dashboard-app-card dashboard-app-link">
          <p>App</p>
          <h3>Your Year Ahead</h3>
          <small>Inverse wrapped demand + temp jobs planning</small>
        </Link>
        <Link to="/events?saved=1" className="dashboard-app-card dashboard-app-link">
          <p>App</p>
          <h3>Saved events</h3>
          <small>{savedIds.length} saved</small>
        </Link>
        <Link to="/settings" className="dashboard-app-card dashboard-app-link">
          <p>App</p>
          <h3>Settings</h3>
          <small>Preferences and defaults</small>
        </Link>
      </section>

      {dashboardError && <div className="dashboard-error">{dashboardError}</div>}

      <section className="dashboard-grid dashboard-grid-two">
        <article className="dashboard-panel">
          <div className="dashboard-panel-head">
            <h2>My recent submissions</h2>
            <Link to="/submit">New submission</Link>
          </div>
          {myEvents.length === 0 ? (
            <p className="dashboard-empty">No submissions yet.</p>
          ) : (
            <ul className="dashboard-list">
              {myEvents.slice(0, 8).map((event) => (
                <li key={event.id}>
                  <strong>{event.title}</strong>
                  <span>{event.start_date} - {event.end_date}</span>
                  <small>{event.status}</small>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="dashboard-panel">
          <div className="dashboard-panel-head">
            <h2>Saved events</h2>
            <Link to="/events?saved=1">Open saved</Link>
          </div>
          {savedEvents.length === 0 ? (
            <p className="dashboard-empty">No saved events yet. Save from the Events page.</p>
          ) : (
            <ul className="dashboard-list">
              {savedEvents.slice(0, 8).map((event) => (
                <li key={event.id}>
                  <strong>{event.title}</strong>
                  <span>{event.start_date} - {event.end_date}</span>
                  <small>{event.location ?? 'Regional'}</small>
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>
    </div>
  );
}
